/**
 * German (DE) UI Translations
 */
import type { UIStrings } from '../types'

export const de: UIStrings = {
  nav: {
    home: 'Startseite',
    about: 'Über mich',
    education: 'Ausbildung',
    experience: 'Erfahrung',
    projects: 'Projekte',
    publications: 'Veröffentlichungen',
    skills: 'Fähigkeiten',
    languages: 'Sprachen',
    certifications: 'Zertifizierungen',
    references: 'Referenzen',
    contact: 'Kontakt'
  },
  sections: {
    hero: 'Willkommen',
    basics: 'Über mich',
    profiles: 'Vernetzen Sie sich mit mir',
    education: 'Ausbildung',
    languages: 'Sprachen',
    certifications: 'Workshops & Zertifizierungen',
    skills: 'Fähigkeiten & Expertise',
    experiences: 'Berufserfahrung',
    projects: 'Projekte & Portfolio',
    publications: 'Veröffentlichungen & Forschung',
    references: 'Referenzen',
    contact: 'Kontakt aufnehmen'
  },
  sectionSubtitles: {
    education: 'Mein akademischer Werdegang und kontinuierlicher Lernweg',
    experiences: 'Eine Zeitleiste von Rollen, Forschung und Beiträgen',
    projects: 'Eine Sammlung von Programmierungs-, Bioinformatik- und Forschungsprojekten',
    references: 'Professionelle Referenzen zur Verifizierung verfügbar. Kontaktdaten werden auf Anfrage für legitime berufliche Anfragen bereitgestellt.'
  },
  actions: {
    download_cv: 'Lebenslauf herunterladen',
    view_more: 'Mehr anzeigen',
    view_less: 'Weniger anzeigen',
    contact_me: 'Kontaktieren Sie mich',
    send_message: 'Nachricht senden',
    book_call: 'Anruf buchen',
    copy: 'Kopieren',
    copied: 'Kopiert!',
    close: 'Schließen',
    expand: 'Erweitern',
    collapse: 'Einklappen',
    filter: 'Filtern',
    search: 'Suchen',
    clear: 'Löschen',
    get_in_touch: 'Kontakt aufnehmen',
    contact: 'Kontakt',
    learn_more: 'Mehr erfahren',
    read_more: 'Mehr lesen',
    show_less: 'Weniger anzeigen',
    show_phone: 'Telefon anzeigen',
    hide_phone: 'Telefon ausblenden',
    email: 'E-Mail',
    phone: 'Telefon',
    expand_all: 'Alle erweitern',
    collapse_all: 'Alle einklappen',
    back_to_top: 'Nach oben',
    retry: 'Erneut versuchen',
    open_pdf: 'PDF öffnen',
    theme: 'Design',
    show_more: 'Mehr anzeigen',
    show_contact: 'Kontakt anzeigen',
    hide_contact: 'Kontakt ausblenden',
    timeline: 'Zeitleiste',
    by_category: 'Nach Kategorie',
    has_link: 'Mit Link'
  },
  forms: {
    name: 'Name',
    email: 'E-Mail',
    subject: 'Betreff',
    message: 'Nachricht',
    send: 'Senden',
    sending: 'Wird gesendet...',
    sent: 'Nachricht gesendet!',
    error: 'Ein Fehler ist aufgetreten',
    success_message: 'Vielen Dank für Ihre Nachricht. Ich werde mich so schnell wie möglich bei Ihnen melden.',
    error_message: 'Beim Senden Ihrer Nachricht ist ein Problem aufgetreten. Bitte versuchen Sie es später erneut oder senden Sie eine direkte E-Mail.',
    contact_description: 'Senden Sie mir eine Nachricht und ich werde mich so schnell wie möglich bei Ihnen melden',
    demo_notice: 'Dies ist ein Demo-Formular. Um mich zu kontaktieren, nutzen Sie bitte die E-Mail-Optionen unten.',
    copy_email: 'E-Mail kopieren',
    email_copied: 'E-Mail-Adresse in die Zwischenablage kopiert!',
    open_email_client: 'E-Mail-Programm öffnen',
    or: 'oder'
  },
  education: {
    institution: 'Institution',
    degree: 'Abschluss',
    field: 'Studienrichtung',
    location: 'Ort',
    duration: 'Dauer',
    gpa: 'Notendurchschnitt',
    present: 'Gegenwart'
  },
  experience: {
    role: 'Rolle',
    institution: 'Organisation',
    duration: 'Dauer',
    present: 'Gegenwart',
    responsibilities: 'Verantwortlichkeiten',
    focus: 'Schwerpunkt'
  },
  projects: {
    view_project: 'Projekt ansehen',
    view_code: 'Code ansehen',
    demo: 'Live-Demo',
    no_projects: 'Keine Projekte gefunden'
  },
  publications: {
    type: 'Typ',
    status: 'Status',
    published: 'Veröffentlicht',
    submitted: 'Eingereicht',
    in_review: 'In Überprüfung',
    waiting: 'Wartet auf Einreichung',
    authors: 'Autoren',
    journal: 'Zeitschrift',
    year: 'Jahr',
    doi: 'DOI',
    isbn: 'ISBN'
  },
  skills: {
    proficiency: 'Kenntnisstand',
    years: 'Jahre Erfahrung',
    category: 'Kategorie',
    all_skills: 'Alle Fähigkeiten',
    skills_across_domains: '{count} Fähigkeiten in {domains} Bereichen',
    no_skills_matching: 'Keine Fähigkeiten gefunden für "{query}"',
    search_placeholder: 'Fähigkeiten suchen...',
    more_categories: '+{count} weitere Kategorien',
    skills_count: '{count} Fähigkeiten'
  },
  languages_section: {
    language: 'Sprache',
    proficiency: 'Kenntnisstand',
    native: 'Muttersprache',
    fluent: 'Fließend',
    professional: 'Beruflich',
    intermediate: 'Mittel',
    beginner: 'Anfänger',
    studying: 'In Ausbildung',
    certification: 'Zertifizierung',
    description: 'Kommunikationsfähigkeiten in mehreren Sprachen'
  },
  references: {
    available_on_request: 'Referenzen auf Anfrage verfügbar',
    position: 'Position',
    department: 'Abteilung',
    institution: 'Institution',
    email: 'E-Mail',
    phone: 'Telefon',
    reference_letter: 'Referenzschreiben',
    privacy_notice: 'Hinweis: Kontaktinformationen sind standardmäßig aus Datenschutzgründen ausgeblendet. Klicken Sie auf "Kontakt anzeigen", um Details anzuzeigen.'
  },
  footer: {
    copyright: '© {year} {name}. Alle Rechte vorbehalten.',
    made_with: 'Erstellt mit',
    built_with: 'Erstellt mit React & TypeScript',
    konami_hint: 'Versuchen Sie den Konami-Code 🎮'
  },
  contact: {
    title: 'Kontaktieren Sie mich',
    description: 'Ob Sie eine Nachricht senden oder einen Anruf vereinbaren möchten, ich bin hier um zu helfen',
    tab_message: 'Nachricht senden',
    tab_booking: 'Anruf buchen'
  },
  errors: {
    something_went_wrong: 'Etwas ist schief gelaufen',
    error_occurred: 'Beim Laden der Anwendung ist ein Fehler aufgetreten',
    error_details: 'Fehlerdetails',
    try_again: 'Erneut versuchen',
    runtime_error: 'Dieser Spark hat einen Laufzeitfehler festgestellt',
    unexpected: 'Beim Ausführen der Anwendung ist etwas Unerwartetes passiert. Die Fehlerdetails werden unten angezeigt. Kontaktieren Sie den Spark-Autor und informieren Sie ihn über dieses Problem.',
    error_details_label: 'Fehlerdetails:'
  },
  filters: {
    all: 'Alle',
    programming: 'Programmierung',
    bioinformatics: 'Bioinformatik',
    biotechnology: 'Biotechnologie',
    academic: 'Akademisch',
    focus: 'Fokus'
  },
  domains: {
    programming: 'Programmierung',
    bioinformatics: 'Bioinformatik',
    biotechnology: 'Biotechnologie',
    academic: 'Akademisch',
    industry: 'Industrie',
    robotics: 'Robotik',
    embedded_systems: 'Eingebettete Systeme',
    student_projects: 'Studentenprojekte'
  },
  empty: {
    no_data: 'Keine Daten verfügbar',
    no_results: 'Keine Ergebnisse gefunden',
    no_education: 'Keine Ausbildungseinträge',
    no_experience: 'Keine Erfahrungseinträge',
    no_projects: 'Keine Projekte',
    no_publications: 'Keine Veröffentlichungen',
    no_skills: 'Keine Fähigkeiten aufgeführt',
    no_languages: 'Keine Sprachen aufgeführt',
    no_certifications: 'Keine Zertifizierungen',
    no_references: 'Keine Referenzen',
    no_profile_data: 'Keine Profildaten verfügbar',
    no_experiences_added: 'Es wurden noch keine beruflichen Erfahrungen hinzugefügt.',
    no_experiences_filter: 'Keine Erfahrungen entsprechen dem aktuellen Fokusfilter. Versuchen Sie "Vollständiger Lebenslauf" auszuwählen, um alle Erfahrungen zu sehen.'
  },
  fallbacks: {
    untitled_role: 'Unbenannte Rolle',
    untitled_project: 'Unbenanntes Projekt',
    untitled_publication: 'Unbenannte Veröffentlichung',
    unknown_institution: 'Unbekannte Institution'
  },
  counts: {
    showing_x_of_y_projects: 'Zeige {current} von {total} Projekten',
    showing_x_of_y_publications: 'Zeige {current} von {total} Veröffentlichung{plural}'
  },
  toasts: {
    copied_to_clipboard: '{label} in Zwischenablage kopiert',
    link_copied: '{network}-Link in Zwischenablage kopiert'
  },
  a11y: {
    skip_to_content: 'Zum Hauptinhalt springen',
    menu: 'Menü',
    close_menu: 'Menü schließen',
    select_language: 'Sprache auswählen',
    select_theme: 'Theme auswählen',
    select_focus: 'Fokus auswählen',
    open_modal: 'Modal öffnen',
    close_modal: 'Modal schließen',
    required: 'erforderlich',
    scroll_to_top: 'Seitenecke - zum Scrollen nach oben klicken',
    quick_actions: 'Schnellaktionen',
    language_carousel: 'Sprachauswahl-Karussell',
    language_selection: 'Sprachauswahl',
    loading_image: 'Bild wird geladen',
    toggle_sidebar: 'Seitenleiste umschalten',
    pagination: 'Seitennummerierung',
    previous_page: 'Zur vorherigen Seite',
    next_page: 'Zur nächsten Seite',
    breadcrumb: 'Brotkrümelnavigation',
    filter_focus: 'Inhaltsfokus filtern',
    testimonial_nav: 'Testimonial-Navigation',
    media_viewer: 'Medienbetrachter',
    previous_media: 'Vorheriges Medium',
    next_media: 'Nächstes Medium',
    scroll_to_about: 'Zum Über-Bereich scrollen',
    certified: 'Zertifiziert',
    workshop: 'Workshop',
    search_certifications: 'Zertifizierungen durchsuchen',
    search_skills: 'Fähigkeiten durchsuchen',
    clear_search: 'Suche löschen',
    list_view: 'Listenansicht',
    carousel_view: 'Karussellansicht',
    search_publications: 'Veröffentlichungen durchsuchen',
    visit_profile: '{network}-Profil besuchen',
    page_scroll_progress: 'Seitenfortschritt',
    previous_skill_category: 'Vorherige Fähigkeitskategorie',
    next_skill_category: 'Nächste Fähigkeitskategorie',
    skill_category_nav: 'Navigation der Fähigkeitskategorien',
    go_to_slide: 'Zu Folie {number} gehen',
    close_quick_actions: 'Schnellaktionen schließen',
    open_quick_actions: 'Schnellaktionen öffnen',
    social_profiles: 'Social-Media-Profile',
    previous_slide: 'Vorherige Folie',
    next_slide: 'Nächste Folie',
    close: 'Schließen'
  },
  media: {
    failed_to_load: 'Bild konnte nicht geladen werden',
    image_could_not_load: 'Das Bild konnte nicht geladen werden',
    click_to_open_pdf: 'Klicken Sie unten, um in einem neuen Tab zu öffnen',
    pdf_document: 'PDF-Dokument'
  },
  profiles: {
    right_click_to_copy: 'Rechtsklick zum Kopieren des Links',
    no_profiles: 'Keine Profile verfügbar'
  },
  booking: {
    title: 'Anruf vereinbaren',
    description: 'Wählen Sie Ihre bevorzugte Besprechungsart und wählen Sie eine passende Zeit',
    meeting_type: 'Besprechungsart',
    video_call: 'Videoanruf',
    phone_call: 'Telefonanruf',
    in_person: 'Persönlich',
    video_description: 'Treffen per Zoom, Google Meet oder Microsoft Teams',
    phone_description: 'Direktes Telefongespräch',
    in_person_description: 'Treffen an einem festgelegten Ort',
    select_date: 'Datum auswählen',
    select_time: 'Uhrzeit auswählen',
    book_call: 'Anruf buchen',
    booking: 'Wird gebucht...',
    clear_selection: 'Auswahl löschen',
    your_selection: 'Ihre Auswahl:',
    success_title: 'Anruf gebucht!',
    success_description: 'Ihr {type} ist für {date} um {time} geplant.',
    error_title: 'Buchung fehlgeschlagen',
    error_description: 'Bei der Buchung Ihres Anrufs ist ein Problem aufgetreten. Bitte versuchen Sie es später erneut.',
    select_date_time: 'Bitte wählen Sie ein Datum und eine Uhrzeit',
    selection_cleared: 'Auswahl gelöscht',
    unavailable: 'nicht verfügbar',
    demo_notice: 'Dies ist eine Demo-Buchungsoberfläche. Um ein echtes Meeting zu planen, nutzen Sie bitte den Link unten.',
    schedule_external: 'Über Calendly planen',
    calendly_cta: 'Terminseite öffnen',
    external_link_notice: 'Sie werden zu einem externen Terminplanungsdienst weitergeleitet.'
  },
  validation: {
    required: '{field} ist erforderlich',
    invalid_email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    min_length_2: '{field} muss mindestens 2 Zeichen lang sein',
    min_length_3: '{field} muss mindestens 3 Zeichen lang sein',
    min_length_10: '{field} muss mindestens 10 Zeichen lang sein'
  },
  loading: {
    loading: 'Wird geladen...',
    please_wait: 'Bitte warten'
  },
  pwa: {
    install_app: 'App installieren',
    installed: 'App installiert',
    offline_mode: 'Sie sind offline',
    update_available: 'Update verfügbar',
    update_now: 'Jetzt aktualisieren'
  },
  download: {
    title: 'Lebenslauf herunterladen',
    description: 'Wählen Sie Ihr bevorzugtes Format und passen Sie den Inhalt an',
    format: 'Format',
    pdf_browser: 'PDF (Browser-Druck)',
    pdf_browser_desc: 'Verwenden Sie den Browser-Druckdialog zum Speichern als PDF',
    json_export: 'JSON-Export',
    json_export_desc: 'Rohdaten im JSON-Format',
    markdown: 'Markdown',
    markdown_desc: 'Lesbares Klartextformat',
    sections: 'Einzubeziehende Abschnitte',
    select_all: 'Alle auswählen',
    deselect_all: 'Alle abwählen',
    include_contact: 'Kontaktdaten einbeziehen',
    include_references: 'Referenzen einbeziehen',
    preview: 'Vorschau',
    download: 'Herunterladen',
    downloading: 'Wird heruntergeladen...',
    success: 'Download abgeschlossen',
    error: 'Download fehlgeschlagen'
  },
  articles: {
    title: 'Artikel & Blog',
    featured: 'Empfohlen',
    read_time: '{minutes} Min. Lesezeit',
    external_link: 'Externer Artikel',
    no_articles: 'Keine Artikel verfügbar',
    filter_by_tag: 'Nach Tag filtern',
    all_tags: 'Alle'
  },
  video: {
    play: 'Video abspielen',
    pause: 'Video pausieren',
    mute: 'Stumm schalten',
    unmute: 'Ton einschalten',
    fullscreen: 'Vollbild',
    exit_fullscreen: 'Vollbild beenden',
    loading: 'Video wird geladen...'
  },
  accessibility: {
    high_contrast: 'Hoher Kontrast',
    font_size: 'Schriftgröße',
    increase_font: 'Schrift vergrößern',
    decrease_font: 'Schrift verkleinern',
    reset_font: 'Schriftgröße zurücksetzen',
    dyslexia_font: 'Legasthenie-freundliche Schrift',
    reduced_motion: 'Reduzierte Bewegung'
  },
  caseStudy: {
    viewCaseStudy: 'Fallstudie ansehen',
    collapse: 'Einklappen',
    problem: 'Problem',
    solution: 'Lösung',
    process: 'Prozess',
    results: 'Ergebnisse',
    technologies: 'Technologien',
    gallery: 'Galerie',
    links: 'Links',
    metrics: 'Wichtige Kennzahlen',
    viewProject: 'Projekt ansehen',
    viewCode: 'Code ansehen',
    liveDemo: 'Live-Demo',
    close: 'Schließen',
    of: 'von',
    previous: 'Vorheriges Bild',
    next: 'Nächstes Bild'
  },
  cookies: {
    title: 'Cookie-Zustimmung',
    message: 'Wir verwenden Cookies, um den Website-Traffic zu analysieren und Ihre Erfahrung zu verbessern. Sie können jederzeit ablehnen.',
    accept: 'Akzeptieren',
    decline: 'Ablehnen',
    dntMessage: 'Wir haben erkannt, dass Sie Do Not Track aktiviert haben. Analytics-Tracking ist deaktiviert.',
    privacyPolicy: 'Datenschutzrichtlinie',
    close: 'Schließen'
  },
  testimonials: {
    title: 'Empfehlungen & Testimonials',
    subtitle: 'Was Kollegen und Kunden sagen',
    linkedIn: 'Auf LinkedIn ansehen',
    noTestimonials: 'Keine Empfehlungen verfügbar',
    previous: 'Vorherige Empfehlung',
    next: 'Nächste Empfehlung',
    pause: 'Automatische Rotation pausieren',
    play: 'Automatische Rotation fortsetzen',
    of: 'von'
  },
  seo: {
    name: 'Ramin Yazdani',
    title: 'Biotechnologe & Entwickler',
    description: 'Lebenslauf von Ramin Yazdani - Biotechnologe und Softwareentwickler',
    separator: ' - '
  }
}
